import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import SearchBar from "../components/activities/SearchBar";

import AppBarMenu from "../components/AppBarMenu";
import ActivitiesList from "../components/activities/ActivitiesList";
import { useFetchUserProvidences } from "../hooks/api/userProvidencesApiHooks";
import { useMinDelay } from "../hooks/ui/useMinDelay";
import { queryParamsUtils } from "../utils/navigationUtils";

//TODO: Fazer um contexto pra isso aqui, pelo amor de Deus
export default function ActivitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMyActivitiesMarked, setIsMyActivitiesMarked] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<any[]>([]);
  const [providencesFilter, setProvidencesFilter] = useState<number[]>([]);
  const [openActivityId, setOpenActivityId] = useState<string | null>(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // Extrair parâmetros da URL - usar useMemo para evitar recriação de arrays/objetos
  const activityId = useMemo(() => queryParamsUtils.getNumber(searchParams, 'activityId'), [searchParams]);
  const shouldOpenActivity = useMemo(() => queryParamsUtils.getBoolean(searchParams, 'openActivity', false), [searchParams]);
  const myActivitiesParam = useMemo(() => queryParamsUtils.getBoolean(searchParams, 'myActivities', true), [searchParams]);
  const providencesFilterParam = useMemo(() => queryParamsUtils.getArray(searchParams, 'providences'), [searchParams]);
  const searchValueParam = useMemo(() => queryParamsUtils.getString(searchParams, 'search'), [searchParams]);

  // Configurar estado inicial baseado nos parâmetros da URL
  useEffect(() => {
    if (myActivitiesParam !== undefined) {
      setIsMyActivitiesMarked(myActivitiesParam);
    }
  }, [myActivitiesParam]);

  useEffect(() => {
    if (providencesFilterParam && providencesFilterParam.length > 0) {
      const parsedIds = providencesFilterParam.map(id => parseInt(id));
      setProvidencesFilter(parsedIds);
    } else if (providencesFilterParam && providencesFilterParam.length === 0) {
      setProvidencesFilter([]);
    }
  }, [providencesFilterParam]);

  useEffect(() => {
    if (searchValueParam) {
      try {
        // Tentar parsear como JSON (caso seja um objeto serializado)
        const parsed = JSON.parse(searchValueParam);
        setSearchValue(Array.isArray(parsed) ? parsed : [parsed]);
      } catch {
        // Se não for JSON, limpar busca (não pode ser uma string simples)
        setSearchValue([]);
      }
    } else {
      setSearchValue([]);
    }
  }, [searchValueParam]);

  // Configurar abertura automática de atividade (apenas na inicialização)
  useEffect(() => {
    if (shouldOpenActivity && activityId !== undefined && activityId !== null) {
      setOpenActivityId(activityId.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executa apenas uma vez na montagem do componente

  // Funções para atualizar URL quando filtros mudarem - usar useCallback para evitar recriação
  const updateMyActivitiesFilter = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('myActivities', value.toString());
      newParams.delete('activityId');
      newParams.delete('openActivity');
      return newParams;
    });
    setOpenActivityId(null);
  }, [setSearchParams]);

  const updateProvidencesFilter = useCallback((providences: number[]) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (providences.length > 0) {
        newParams.set('providences', providences.join(','));
      } else {
        newParams.delete('providences');
      }
      newParams.delete('activityId');
      newParams.delete('openActivity');
      return newParams;
    });
    setOpenActivityId(null);
  }, [setSearchParams]);

  const updateSearchValue = useCallback((search: any) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (search) {
        newParams.set('search', typeof search === 'string' ? search : JSON.stringify(search));
      } else {
        newParams.delete('search');
      }
      newParams.delete('activityId');
      newParams.delete('openActivity');
      return newParams;
    });
    setOpenActivityId(null);
  }, [setSearchParams]);

  const { userProvidencesLoading, userProvidencesData } = useFetchUserProvidences({
    userId: user.id,
    providencesFilter,
    searchValue,
    includeAllActivities: !isMyActivitiesMarked,
  });

  // Aplicar um pequeno delay para suavizar o loading quando filtros mudam
  const isLoadingWithDelay = useMinDelay(userProvidencesLoading, 400);

  // Não filtrar por projectId - mostrar todas as atividades mas destacar a específica
  const filteredActivitiesData = userProvidencesData;

  return (
    <AppBarMenu icons={null}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Painel único unificado */}
            <Paper
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: 600,
                boxShadow: 3,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {/* Cabeçalho de conteúdo integrado */}
              <Box sx={{ px: 3, pt: 3, pb: 0 }}>
                <SearchBar
                  setProvidencesFilter={updateProvidencesFilter}
                  providencesFilter={providencesFilter}
                  data={userProvidencesData}
                  isMyActivitiesMarked={isMyActivitiesMarked}
                  setIsMyActivitiesMarked={updateMyActivitiesFilter}
                  searchValue={searchValue}
                  setSearchValue={updateSearchValue}
                />
              </Box>

              {/* Conteúdo da lista */}
              {isLoadingWithDelay ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                  }}
                >
                  <CircularProgress size={80} />
                </Box>
              ) : (
                <Box sx={{ px: 3, pb: 3 }}>
                  <ActivitiesList 
                    data={filteredActivitiesData} 
                    openActivityId={openActivityId}
                    onActivityOpened={() => setOpenActivityId(null)}
                    highlightActivityId={activityId}
                    scrollToActivity={true}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </AppBarMenu>
  );
}
